// Policy engine (spec §99–§100): categories, parties, tags, colours, premium frequency
// equivalents, effective-dated premium adjustments with full history, reversals and validated
// status transitions. Pure functions + commands returning changesets.
import { POLICY_DEPENDENT_TABLES, allRowsOf, type Changeset, type Put, type WorkspaceView } from './changeset.ts';
import { DomainError, livePolicy, created, put, touched, clean, opt, type Ctx } from './cmd.ts';
import { addMonths, diffDays, type Day } from './dates.ts';
import { PER_YEAR, divideMoney, fromMinor, isRecurring, multiplyMoney, roundMoney, toMinor, type Frequency } from './money.ts';
import { regionPack } from './regions.ts';
import type { Policy, PolicyAdjustment, PolicyCategory, PolicyStatus, TableName } from './schema.ts';
import { getSetting } from './services.ts';

// --- categories, badges, colours ---------------------------------------------------------
const CATEGORY_PATTERNS: [PolicyCategory, RegExp][] = [
  ['medical', /medic|health|hospital|ιατρ|υγε[ιί]|νοσοκ/i],
  ['motor', /motor|car\b|vehicle|auto\b|αυτοκ[ιί]ν|οχ[ηή]μ/i],
  ['home', /home|house|household|dwelling|κατοικ|σπ[ιί]τ|οικ[ιί]α/i],
  ['travel', /travel|ταξ[ιί]δ/i],
  ['pension', /pension|retire|provident|σ[υύ]νταξ|ταμε[ιί]ο προν/i],
  ['education', /educat|student|school|college|σπουδ|εκπα[ιί]δ|φοιτ/i],
  ['critical_illness', /critical|dread|σοβαρ[ώο]ν ασθ/i],
  ['disability', /disab|income prot|αναπηρ|ανικαν/i],
  ['credit_life', /credit|mortgage|loan|δαν[εε]ι|στεγαστ/i],
  ['accident', /accident|ατ[υύ]χημ/i],
  ['savings', /saving|invest|unit.?link|αποταμ|επενδ/i],
  ['liability', /liabil|αστικ[ήη] ευθ/i],
  ['business', /business|company|corporate|key.?man|επιχε[ιί]ρ/i],
  ['group', /\bgroup\b|ομαδικ/i],
];
/** Explicit category wins; legacy rows without one are inferred from the product name and branch (display only, never stored silently). */
export function policyCategory(p: Pick<Policy, 'category' | 'productName' | 'branch'>): PolicyCategory {
  if (p.category) return p.category;
  for (const [c, re] of CATEGORY_PATTERNS) if (re.test(p.productName ?? '')) return c;
  return p.branch === 'general' ? 'other' : 'life';
}
export const LIFE_CATEGORIES: readonly PolicyCategory[] = ['life', 'medical', 'savings', 'pension', 'education', 'accident', 'critical_illness', 'disability', 'credit_life', 'group'];

/** Distinct hues per category (text badge always carries the meaning; colour is a secondary cue). */
export const CATEGORY_COLORS: Record<PolicyCategory, string> = {
  life: '#3b6fd8', medical: '#15a38a', savings: '#c9971c', pension: '#8a5cd6', education: '#e0701a', accident: '#d9483b',
  critical_illness: '#b8386b', disability: '#6d7fa3', credit_life: '#2f8fb5', motor: '#556b2f', home: '#8b5e3c', travel: '#1c9fcf',
  liability: '#7a6a58', business: '#39475e', group: '#5a6fb0', other: '#7b8494',
};
export type ColorSource = 'manual' | 'category' | 'insurer';
export function policyColor(p: Pick<Policy, 'color' | 'category' | 'productName' | 'branch' | 'carrier'>, opts: { precedence?: ColorSource[]; insurerColors?: Record<string, string> } = {}): { color: string; source: ColorSource } {
  const order = opts.precedence?.length ? opts.precedence : (['manual', 'category', 'insurer'] as ColorSource[]);
  for (const s of order) {
    if (s === 'manual' && p.color && /^#[0-9a-f]{6}$/i.test(p.color)) return { color: p.color, source: 'manual' };
    if (s === 'insurer' && p.carrier && opts.insurerColors?.[p.carrier]) return { color: opts.insurerColors[p.carrier]!, source: 'insurer' };
    if (s === 'category') return { color: CATEGORY_COLORS[policyCategory(p)], source: 'category' };
  }
  return { color: CATEGORY_COLORS[policyCategory(p)], source: 'category' };
}

// --- smart tags: the needs one product may serve (a finding aid, not proof of cover) -----
export const NEED_TAGS = ['life_cover', 'savings', 'education', 'child_protection', 'retirement', 'medical', 'accident', 'income_protection', 'critical_illness', 'mortgage_protection', 'property', 'motor', 'travel', 'liability', 'business'] as const;
export type NeedTag = typeof NEED_TAGS[number];
const CATEGORY_TAGS: Record<PolicyCategory, NeedTag[]> = {
  life: ['life_cover'], medical: ['medical'], savings: ['savings'], pension: ['retirement', 'savings'], education: ['education', 'savings'],
  accident: ['accident'], critical_illness: ['critical_illness'], disability: ['income_protection'], credit_life: ['mortgage_protection', 'life_cover'],
  motor: ['motor'], home: ['property'], travel: ['travel'], liability: ['liability'], business: ['business'], group: [], other: [],
};
export function suggestTags(p: Pick<Policy, 'category' | 'productName' | 'branch' | 'benefits' | 'parties' | 'personId' | 'insuredPersonId'>): NeedTag[] {
  const out = new Set<NeedTag>(CATEGORY_TAGS[policyCategory(p)]);
  const name = p.productName ?? '';
  if (/student|child|kid|junior|future|παιδ|μαθητ|φοιτ/i.test(name)) { out.add('education'); out.add('child_protection'); out.add('savings'); }
  if (/life|ζω[ηή]ς|term\b/i.test(name)) out.add('life_cover');
  for (const b of p.benefits ?? []) {
    const k = (b.kind ?? '').toLowerCase();
    if (/death|life/.test(k)) out.add('life_cover');
    if (/critical/.test(k)) out.add('critical_illness');
    if (/disab|income|waiver/.test(k)) out.add('income_protection');
    if (/medical|hospital/.test(k)) out.add('medical');
    if (/accident/.test(k)) out.add('accident');
    if (/saving|maturity|fund/.test(k)) out.add('savings');
  }
  const insuredChild = (p.parties ?? []).some((x) => x.role === 'insured' && x.personId && x.personId !== p.personId) || (!!p.insuredPersonId && p.insuredPersonId !== p.personId);
  if (insuredChild && out.has('education')) out.add('child_protection');
  return NEED_TAGS.filter((t) => out.has(t));
}

// --- parties --------------------------------------------------------------------------
/** Everyone a policy touches, by role. The row's personId is the owner/policyholder unless parties say otherwise. */
export function policyParties(p: Pick<Policy, 'personId' | 'insuredPersonId' | 'insuredName' | 'payerName' | 'parties'>) {
  const list = [...(p.parties ?? [])];
  if (!list.some((x) => x.role === 'owner')) list.unshift({ role: 'owner', personId: p.personId });
  // no insured person named → the holder is the insured; insuredPersonId is an insured person even next to listed parties
  if (!list.some((x) => x.role === 'insured')) list.push({ role: 'insured', personId: p.insuredPersonId ?? p.personId, name: p.insuredName ?? null });
  else if (p.insuredPersonId && !list.some((x) => x.role === 'insured' && x.personId === p.insuredPersonId)) list.push({ role: 'insured', personId: p.insuredPersonId, name: p.insuredName ?? null });
  if (p.payerName && !list.some((x) => x.role === 'payer')) list.push({ role: 'payer', name: p.payerName });
  return list;
}
/** person ids insured by the policy (a group or family policy can insure several) */
export const insuredIds = (p: Parameters<typeof policyParties>[0]) => [...new Set(policyParties(p).filter((x) => x.role === 'insured' && x.personId).map((x) => x.personId!))];
/** policies where the person is owner, payer or insured — each policy once (no duplicate totals) */
export function policiesOfPerson(v: WorkspaceView, personId: string): Policy[] {
  const seen = new Map<string, Policy>();
  for (const p of v.where('policies', 'personId', personId)) seen.set(p.id, p);
  for (const p of v.rows('policies')) if (!seen.has(p.id) && policyParties(p).some((x) => x.personId === personId)) seen.set(p.id, p);
  return [...seen.values()];
}

// --- premiums -------------------------------------------------------------------------
export const currencyOf = (p: Pick<Policy, 'currency'>, region?: string | null) => (p.currency || regionPack(region).currency).toUpperCase();
const effective = (a: Pick<PolicyAdjustment, 'date'>, asOf: Day) => !a.date || a.date <= asOf;

/** Annual premium in force on `asOf`: base + adjustments effective on or before that day. Future changes never alter today. */
export function premiumAt(p: Pick<Policy, 'annualPremium' | 'currency'>, adjustments: readonly Pick<PolicyAdjustment, 'annualAmount' | 'date'>[], asOf: Day): number {
  const c = p.currency;
  return fromMinor(toMinor(p.annualPremium || 0, c) + adjustments.filter((a) => effective(a, asOf)).reduce((s, a) => s + toMinor(a.annualAmount || 0, c), 0), c);
}

export interface PremiumHistoryLine { id: string; date: string | null; type: string; previous: number | null; next: number | null; delta: number; reason: string | null; reversed: boolean; reversal: boolean; future: boolean; source: string | null }
export interface PremiumView {
  currency: string; frequency: Frequency | null; recurring: boolean;
  /** original (base) premium: annual for recurring policies, the one-off amount for single premium */
  original: number;
  current: number;
  annualEquivalent: number | null; monthlyEquivalent: number | null;
  instalment: number | null; instalmentQuoted: boolean;
  singlePremium: number | null;
  pending: PremiumHistoryLine[]; history: PremiumHistoryLine[];
  startDate: string | null; endDate: string | null; durationMonths: number | null; remainingMonths: number | null;
}
function monthsBetween(a: Day, b: Day): number { return Math.max(0, (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7)) - (Number(b.slice(8, 10)) < Number(a.slice(8, 10)) ? 1 : 0)); }

export function premiumView(p: Policy, adjustments: readonly PolicyAdjustment[], asOf: Day, region?: string | null): PremiumView {
  const currency = currencyOf(p, region);
  const frequency = (p.paymentFrequency ?? null) as Frequency | null;
  const recurring = frequency ? isRecurring(frequency) : true;
  const current = premiumAt({ annualPremium: p.annualPremium, currency }, adjustments, asOf);
  const reversedIds = new Set(adjustments.filter((a) => a.reversesId).map((a) => a.reversesId!));
  const lines: PremiumHistoryLine[] = [...adjustments].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '') || a.createdAt.localeCompare(b.createdAt)).map((a) => ({
    id: a.id, date: a.date ?? null, type: a.reversesId ? 'reversal' : a.type, previous: a.previousAnnual ?? null, next: a.newAnnual ?? null, delta: a.annualAmount,
    reason: a.reason ?? a.note ?? null, reversed: reversedIds.has(a.id), reversal: !!a.reversesId, future: !!a.date && a.date > asOf, source: a.source ?? null,
  }));
  const perYear = frequency && recurring ? PER_YEAR[frequency] : null;
  const annualEquivalent = recurring ? current : null;
  const quotedShift = perYear && p.instalment != null ? divideMoney(current - (p.annualPremium || 0), perYear, currency) : 0;
  const instalment = !recurring ? null : p.instalment != null && perYear ? roundMoney(p.instalment + quotedShift, currency) : perYear ? divideMoney(current, perYear, currency) : null;
  const start = p.startDate ?? p.issueDate ?? null;
  const end = p.endDate ?? null;
  return {
    currency, frequency, recurring, original: p.annualPremium || 0, current,
    annualEquivalent, monthlyEquivalent: annualEquivalent === null ? null : divideMoney(annualEquivalent, 12, currency),
    instalment, instalmentQuoted: recurring && p.instalment != null, singlePremium: recurring ? null : current,
    // a future change that was reversed (and its reversal) is no longer pending — both stay in history
    pending: lines.filter((l) => l.future && !l.reversed && !l.reversal), history: lines.filter((l) => !l.future || l.reversed || l.reversal),
    startDate: start, endDate: end,
    durationMonths: start && end ? monthsBetween(start, end) : null,
    remainingMonths: end ? (end <= asOf ? 0 : monthsBetween(asOf, end)) : null,
  };
}
/** view helper used across the app: annual premium in force today (recurring) — the old currentAnnualPremium but date-aware */
export function annualNow(v: WorkspaceView, p: Policy, today: Day): number {
  return premiumAt(p, v.where('policy_adjustments', 'policyId', p.id), today);
}

// --- validated status transitions (spec §100) --------------------------------------------
export const POLICY_TRANSITIONS: Record<PolicyStatus, readonly PolicyStatus[]> = {
  offer: ['pending', 'active', 'cancelled'],
  pending: ['offer', 'active', 'cancelled'],
  active: ['renewed', 'lapsed', 'cancelled', 'matured'],
  renewed: ['active', 'renewed', 'lapsed', 'cancelled', 'matured'],
  lapsed: ['active', 'cancelled'],
  cancelled: ['active'],
  matured: [],
};
export const canTransition = (from: PolicyStatus, to: PolicyStatus) => from === to || POLICY_TRANSITIONS[from].includes(to);

// --- adjustments ------------------------------------------------------------------------
export type AdjustMode = 'new_instalment' | 'new_annual' | 'delta_instalment' | 'delta_annual';
export interface AdjustInput { policyId: string; effectiveDate: Day; mode: AdjustMode; amount: number; reason?: string; source?: string; campaignId?: string | null }

/** Compute an adjustment without writing it (preview in the UI and in tests). */
export function previewAdjustment(v: WorkspaceView, input: AdjustInput, region?: string | null) {
  const p = livePolicy(v, input.policyId);
  if (!input.effectiveDate) throw new DomainError('adjustment.date_required');
  if (!Number.isFinite(input.amount)) throw new DomainError('adjustment.amount_invalid');
  const currency = currencyOf(p, region);
  const freq = (p.paymentFrequency ?? 'annual') as Frequency;
  const recurring = isRecurring(freq) || !p.paymentFrequency;
  const perYear = recurring ? (p.paymentFrequency ? PER_YEAR[freq] : 1) : 1;
  if (!recurring && input.mode.endsWith('instalment')) throw new DomainError('adjustment.single_premium_instalment');
  const adj = v.where('policy_adjustments', 'policyId', p.id);
  const previous = premiumAt({ annualPremium: p.annualPremium, currency }, adj, input.effectiveDate);
  let next: number;
  switch (input.mode) {
    case 'new_instalment': next = multiplyMoney(input.amount, perYear, currency); break;
    case 'new_annual': next = roundMoney(input.amount, currency); break;
    case 'delta_instalment': next = fromMinor(toMinor(previous, currency) + toMinor(multiplyMoney(input.amount, perYear, currency), currency), currency); break;
    case 'delta_annual': next = fromMinor(toMinor(previous, currency) + toMinor(input.amount, currency), currency); break;
  }
  if (next < 0) throw new DomainError('adjustment.negative');
  const delta = fromMinor(toMinor(next, currency) - toMinor(previous, currency), currency);
  if (delta === 0) throw new DomainError('adjustment.no_change');
  return { policy: p, currency, previous, next, delta, perYear: recurring ? perYear : null, type: delta > 0 ? 'increase' : 'decrease', recurring };
}

/**
 * Record a premium increase or decrease effective on a date. The original premium, previous premium,
 * signed delta, new premium, reason, source and actor are all kept. The policy row itself is not
 * rewritten (its base premium stays the original); the current premium is derived by effective date.
 * Production is credited for the delta only (see production.ts).
 */
export function adjustPremium(v: WorkspaceView, input: AdjustInput, ctx: Ctx, productionPuts?: (adj: PolicyAdjustment) => Put[]): Changeset {
  const pv = previewAdjustment(v, input, ctx.region);
  const row = created<'policy_adjustments'>(ctx, {
    policyId: pv.policy.id, date: input.effectiveDate, type: pv.type, annualAmount: pv.delta, previousAnnual: pv.previous, newAnnual: pv.next,
    reason: opt(input.reason), source: opt(input.source) ?? 'advisor', actor: ctx.actor, note: null,
    data: { backdated: input.effectiveDate < ctx.today, campaignId: input.campaignId ?? null },
  });
  const puts: Put[] = [put('policy_adjustments', row), put('timeline', created<'timeline'>(ctx, {
    personId: pv.policy.personId, at: ctx.now, type: 'policy', title: pv.type === 'increase' ? 'timeline.premium_increase' : 'timeline.premium_decrease',
    body: `${pv.policy.productName}: ${pv.previous} → ${pv.next} ${pv.currency} (${input.effectiveDate})`, refTable: 'policies', refId: pv.policy.id,
    data: { kind: 'premium_changed', previous: pv.previous, next: pv.next, delta: pv.delta, currency: pv.currency, effectiveDate: input.effectiveDate, adjustmentId: row.id },
  }))];
  if (productionPuts) puts.push(...productionPuts(row));
  return { action: `policy.${pv.type}`, entity: 'policy_adjustments', entityId: row.id, summary: pv.policy.productName, puts, deletes: [] };
}

/** Cancel an adjustment by a linked reversal — the original stays in history. */
export function reverseAdjustment(v: WorkspaceView, adjustmentId: string, reason: string, ctx: Ctx, productionPuts?: (rev: PolicyAdjustment, original: PolicyAdjustment) => Put[]): Changeset {
  const a = v.get('policy_adjustments', adjustmentId);
  if (!a) throw new DomainError('adjustment.not_found');
  if (a.reversesId) throw new DomainError('adjustment.is_reversal');
  if (v.rows('policy_adjustments').some((x) => x.reversesId === a.id)) throw new DomainError('adjustment.already_reversed');
  if (!clean(reason)) throw new DomainError('adjustment.reason_required');
  const p = livePolicy(v, a.policyId);
  const rev = created<'policy_adjustments'>(ctx, {
    policyId: a.policyId, date: a.date ?? ctx.today, type: 'reversal', annualAmount: -a.annualAmount, previousAnnual: a.newAnnual ?? null, newAnnual: a.previousAnnual ?? null,
    reason: clean(reason), source: 'advisor', actor: ctx.actor, reversesId: a.id, note: null,
  });
  const puts: Put[] = [put('policy_adjustments', rev), put('timeline', created<'timeline'>(ctx, {
    personId: p.personId, at: ctx.now, type: 'policy', title: 'timeline.premium_reversal', body: `${p.productName}: ${clean(reason)}`, refTable: 'policies', refId: p.id,
    data: { kind: 'adjustment_reversed', adjustmentId: a.id, reversalId: rev.id, delta: -a.annualAmount },
  }))];
  if (productionPuts) puts.push(...productionPuts(rev, a));
  return { action: 'policy.adjustment_reversed', entity: 'policy_adjustments', entityId: rev.id, summary: p.productName, puts, deletes: [] };
}

/** Change the status with a validated transition; the prior status, date and reason go to the policy timeline. */
export function changePolicyStatus(v: WorkspaceView, id: string, input: { status: PolicyStatus; date?: Day | null; reason?: string }, ctx: Ctx, productionPuts?: (before: Policy, after: Policy) => Put[]): Changeset {
  const p = livePolicy(v, id);
  if (p.status === input.status) throw new DomainError('policy.same_status');
  if (!canTransition(p.status, input.status)) throw new DomainError('policy.invalid_transition');
  const date = input.date ?? ctx.today;
  const patch: Partial<Policy> = { status: input.status };
  if (input.status === 'cancelled' || input.status === 'lapsed' || input.status === 'matured') patch.data = { ...(p.data ?? {}), [`${input.status}At`]: date };
  if ((input.status === 'active') && (p.status === 'lapsed' || p.status === 'cancelled')) patch.data = { ...(p.data ?? {}), reinstatedAt: date };
  if (input.status === 'renewed') patch.renewalDate = addMonths(p.renewalDate ?? p.startDate ?? date, 12);
  const next = touched(ctx, p, patch);
  const puts: Put[] = [put('policies', next), put('timeline', created<'timeline'>(ctx, {
    personId: p.personId, at: ctx.now, type: 'policy', title: `timeline.policy_${input.status}`, body: `${p.productName}${input.reason ? ' — ' + clean(input.reason) : ''}`,
    refTable: 'policies', refId: p.id, data: { kind: 'status_changed', from: p.status, to: input.status, date, reason: opt(input.reason) },
  }))];
  if (productionPuts) puts.push(...productionPuts(p, next));
  return { action: `policy.status.${input.status}`, entity: 'policies', entityId: id, summary: p.productName, puts, deletes: [] };
}

/** Policy timeline: status changes, premium changes, claims, renewals, documents — one chronological list. */
export function policyTimeline(v: WorkspaceView, policyId: string) {
  return v.rows('timeline').filter((e) => e.refTable === 'policies' && e.refId === policyId).sort((a, b) => b.at.localeCompare(a.at));
}

/** days since the policy started (for cancellation windows) */
export const policyAgeDays = (p: Pick<Policy, 'startDate' | 'issueDate' | 'createdAt'>, on: Day) => diffDays((p.startDate ?? p.issueDate ?? p.createdAt.slice(0, 10)), on);
export const colorPrecedenceOf = (v: WorkspaceView) => getSetting<ColorSource[]>(v, 'policy.colorPrecedence', ['manual', 'category', 'insurer']);
export const insurerColorsOf = (v: WorkspaceView) => getSetting<Record<string, string>>(v, 'policy.insurerColors', {});

// --- v13: «Διαγραφή λανθασμένου συμβολαίου» — soft deletion with server step-up, controlled restore ------------------
// A policy entered by mistake can be deleted, but never hard-deleted: the row stays (record retention) with
// deletedAt / deletedBy / deleteReason, and DatasetView hides it and every record that depends on it (premium
// changes, claims, documents, tasks, renewals, production, commission, arrears) from lists, reports and statistics.
// Both commands need a short-lived single-use grant from the platform (password or passkey + a fresh authenticator
// code); the sync server refuses the change without it. The local check below is a convenience, not the security.

/** days within which a deleted policy can be restored (afterwards it stays hidden but retained) */
export const POLICY_RESTORE_DAYS = 30;
export interface DeletionEntry { action: 'deleted' | 'restored'; at: string; by: string; reason: string | null }
export interface PolicyIdentity {
  id: string; insurer: string | null; product: string; number: string | null; holder: string; holderId: string;
  insured: string[]; startDate: string | null; issueDate: string | null; endDate: string | null; renewalDate: string | null;
  status: PolicyStatus; category: PolicyCategory; annualPremium: number; currency: string | null;
}
/** exactly which policy is meant — shown before the final confirmation */
export function policyIdentity(v: WorkspaceView, id: string): PolicyIdentity {
  const p = v.get('policies', id);
  if (!p) throw new DomainError('policy.not_found');
  const name = (pid?: string | null, n?: string | null) => (pid ? v.get('persons', pid)?.displayName : null) ?? n ?? null;
  const insured = [...new Set(policyParties(p).filter((x) => x.role === 'insured').map((x) => name(x.personId, x.name)).filter((x): x is string => !!x))];
  return {
    id: p.id, insurer: p.carrier ?? null, product: p.productName, number: p.policyNumber ?? null, holder: name(p.personId) ?? '—', holderId: p.personId,
    insured, startDate: p.startDate ?? null, issueDate: p.issueDate ?? null, endDate: p.endDate ?? null, renewalDate: p.renewalDate ?? null,
    status: p.status, category: policyCategory(p), annualPremium: p.annualPremium, currency: p.currency ?? null,
  };
}
/** records linked to the policy: hidden together with it (dependent) or kept visible as history (cases, sale files) */
export function policyLinkedRecords(v: WorkspaceView, id: string): { hidden: Partial<Record<TableName, number>>; kept: Partial<Record<TableName, number>> } {
  const count = (t: TableName) => (allRowsOf(v, t) as unknown as { policyId?: string | null }[]).filter((r) => r.policyId === id).length;
  const hidden: Partial<Record<TableName, number>> = {}, kept: Partial<Record<TableName, number>> = {};
  for (const t of POLICY_DEPENDENT_TABLES) { const n = count(t); if (n) hidden[t] = n; }
  for (const t of ['cases', 'sale_files'] as const) { const n = count(t); if (n) kept[t] = n; }
  return { hidden, kept };
}
const deletionHistory = (p: Policy) => (((p.data ?? {}) as { deletionHistory?: DeletionEntry[] }).deletionHistory ?? []);
/** last moment (ISO timestamp) a deleted policy can be restored; null when it is not deleted */
export function restoreDeadline(p: Pick<Policy, 'deletedAt'>): string | null {
  return p.deletedAt ? new Date(new Date(p.deletedAt).getTime() + POLICY_RESTORE_DAYS * 864e5).toISOString() : null;
}
export const canRestorePolicy = (p: Pick<Policy, 'deletedAt'>, nowIso: string) => !!p.deletedAt && restoreDeadline(p)! >= nowIso;
/** soft-deleted policies (most recent first), optionally of one person — the «Διαγραμμένα» list with restore */
export function deletedPolicies(v: WorkspaceView, personId?: string): Policy[] {
  return allRowsOf(v, 'policies').filter((p) => p.deletedAt && (!personId || p.personId === personId || policyParties(p).some((x) => x.personId === personId)))
    .sort((a, b) => b.deletedAt!.localeCompare(a.deletedAt!));
}
export interface PolicyDeleteInput { reason: string; grant: string; by: string }
export function deletePolicy(v: WorkspaceView, id: string, input: PolicyDeleteInput, ctx: Ctx): Changeset {
  const p = v.get('policies', id);
  if (!p) throw new DomainError('policy.not_found');
  if (p.deletedAt) throw new DomainError('policy.already_deleted');
  const reason = clean(input.reason);
  if (reason.length < 3) throw new DomainError('policy.delete_reason_required');
  if (!clean(input.grant)) throw new DomainError('policy.stepup_required');
  const by = clean(input.by) || ctx.actor;
  const next = touched(ctx, p, { deletedAt: ctx.now, deletedBy: by, deleteReason: reason, data: { ...(p.data ?? {}), deletionHistory: [...deletionHistory(p), { action: 'deleted', at: ctx.now, by, reason }] } });
  const puts: Put[] = [put('policies', next), put('timeline', created<'timeline'>(ctx, {
    personId: p.personId, at: ctx.now, type: 'policy', title: 'timeline.policy_deleted', body: `${p.productName}${p.policyNumber ? ` · ${p.policyNumber}` : ''} — ${reason}`,
    refTable: 'policies', refId: p.id, data: { kind: 'policy_deleted', by, reason, restoreUntil: restoreDeadline(next) },
  }))];
  return { action: 'policy.delete', entity: 'policies', entityId: id, summary: p.productName, puts, deletes: [], grants: { [id]: clean(input.grant) }, noUndo: true };
}
export function restorePolicy(v: WorkspaceView, id: string, input: { grant: string; by: string; reason?: string }, ctx: Ctx): Changeset {
  const p = v.get('policies', id);
  if (!p) throw new DomainError('policy.not_found');
  if (!p.deletedAt) throw new DomainError('policy.not_deleted');
  if (!canRestorePolicy(p, ctx.now)) throw new DomainError('policy.restore_window_passed');
  if (!clean(input.grant)) throw new DomainError('policy.stepup_required');
  const by = clean(input.by) || ctx.actor;
  const next = touched(ctx, p, { deletedAt: null, deletedBy: null, deleteReason: null, data: { ...(p.data ?? {}), deletionHistory: [...deletionHistory(p), { action: 'restored', at: ctx.now, by, reason: opt(input.reason) }] } });
  const puts: Put[] = [put('policies', next), put('timeline', created<'timeline'>(ctx, {
    personId: p.personId, at: ctx.now, type: 'policy', title: 'timeline.policy_restored', body: `${p.productName}${p.policyNumber ? ` · ${p.policyNumber}` : ''}`,
    refTable: 'policies', refId: p.id, data: { kind: 'policy_restored', by, deletedAt: p.deletedAt },
  }))];
  return { action: 'policy.restore', entity: 'policies', entityId: id, summary: p.productName, puts, deletes: [], grants: { [id]: clean(input.grant) }, noUndo: true };
}
/**
 * Without an account (pure offline workspace) a deletion cannot be secured by a server, so it is not offered:
 * the policy is marked «Ακυρωμένο — καταχωρίστηκε κατά λάθος» instead (status cancelled + flag, history kept visible).
 */
export function markPolicyEnteredInError(v: WorkspaceView, id: string, reason: string, ctx: Ctx, productionPuts?: (before: Policy, after: Policy) => Put[]): Changeset {
  const p = livePolicy(v, id);
  const why = clean(reason);
  if (why.length < 3) throw new DomainError('policy.delete_reason_required');
  const flag = { enteredInError: true, enteredInErrorAt: ctx.now, enteredInErrorReason: why };
  if (p.status === 'cancelled') {
    const next = touched(ctx, p, { data: { ...(p.data ?? {}), ...flag } });
    return { action: 'policy.entered_in_error', entity: 'policies', entityId: id, summary: p.productName, deletes: [], puts: [put('policies', next), put('timeline', created<'timeline'>(ctx, {
      personId: p.personId, at: ctx.now, type: 'policy', title: 'timeline.policy_entered_in_error', body: `${p.productName} — ${why}`, refTable: 'policies', refId: p.id, data: { kind: 'entered_in_error', reason: why },
    }))] };
  }
  const cs = changePolicyStatus(v, id, { status: 'cancelled', date: ctx.today, reason: why }, ctx, productionPuts);
  const row = cs.puts[0]!.row as Policy;
  cs.puts[0] = put('policies', { ...row, data: { ...(row.data ?? {}), ...flag } });
  return { ...cs, action: 'policy.entered_in_error' };
}
export const isEnteredInError = (p: Pick<Policy, 'data'>) => !!(p.data as { enteredInError?: boolean } | null | undefined)?.enteredInError;
